/**
 * HVP Update Scheduler - two-phase flush
 *
 * Phase 1 DOM updates (priority ordered), paint wait, Phase 2 UI updates.
 * emitUIUpdate() dispatches hvp_ui_update on WINDOW - HVPUIManager and the
 * theme cascade lock listen there.
 *
 * Split out of update-scheduler.js (350-line rule): the methods below moved
 * VERBATIM and are installed on HVPUpdateScheduler.prototype by
 * update-scheduler.js, so `this` is the same instance and behavior is identical.
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

export const SchedulerFlushMethods = {
    /**
     * Execute all pending updates in two phases
     *
     * Phase 1: Execute DOM updates (sorted by priority)
     * Phase 2: Wait for paint, then execute UI updates
     */
    async flush() {
        if (this.isFlushing) {
            this.log('Already flushing, skipping');
            return;
        }

        this.isFlushing = true;
        this.flushScheduled = false;
        const startTime = performance.now();

        this.log('--- FLUSH START ---');
        this.log(`DOM updates: ${this.domUpdates.length}, UI updates: ${this.uiUpdates.size}`);

        try {
            // PHASE 1: Execute DOM updates
            await this.executeDOMUpdates();

            // Wait for browser paint
            await this.waitForPaint();

            // PHASE 2: Execute UI updates
            await this.executeUIUpdates();

        } catch (error) {
            console.error('[HVP Scheduler] Flush error:', error);
        } finally {
            this.isFlushing = false;

            const elapsed = performance.now() - startTime;
            this.stats.totalFlushTime += elapsed;
            this.stats.lastFlushTime = elapsed;
            this.stats.flushCount++;

            this.log(`--- FLUSH END (${elapsed.toFixed(2)}ms) ---`);

            // If more updates were scheduled during flush, process them
            if (this.domUpdates.length > 0 || this.uiUpdates.size > 0) {
                this.scheduleFlush();
            }
        }
    },

    /**
     * Execute all DOM updates in priority order
     */
    async executeDOMUpdates() {
        if (this.domUpdates.length === 0) {
            return;
        }

        // Sort by priority (lower number = higher priority)
        this.domUpdates.sort((a, b) => a.priority - b.priority);

        this.log(`Executing ${this.domUpdates.length} DOM updates`);

        // Execute all updates
        const updates = [...this.domUpdates];
        this.domUpdates = [];

        for (const { fn, description, resolve, reject } of updates) {
            try {
                const result = await fn();
                this.stats.domUpdateCount++;
                resolve(result);
            } catch (error) {
                console.error(`[HVP Scheduler] DOM update failed: ${description}`, error);
                reject(error);
            }
        }
    },

    /**
     * Wait for browser to paint
     *
     * Uses double requestAnimationFrame to ensure paint has occurred.
     */
    waitForPaint() {
        return new Promise(resolve => {
            requestAnimationFrame(() => {
                requestAnimationFrame(resolve);
            });
        });
    },

    /**
     * Execute all UI updates
     */
    async executeUIUpdates() {
        if (this.uiUpdates.size === 0) {
            return;
        }

        this.log(`Executing ${this.uiUpdates.size} UI updates`);

        const updates = Array.from(this.uiUpdates);
        this.uiUpdates.clear();

        for (const { type, source, callback } of updates) {
            try {
                // Execute callback if provided
                if (typeof callback === 'function') {
                    await callback();
                }

                // Emit UI update event
                this.emitUIUpdate(type, source);
                this.stats.uiUpdateCount++;

            } catch (error) {
                console.error(`[HVP Scheduler] UI update failed: ${type}`, error);
            }
        }
    },

    /**
     * Emit UI update event
     *
     * @param {string} type - Update type
     * @param {string} source - Update source
     */
    emitUIUpdate(type, source) {
        // Call registered callback
        if (typeof this.onUIUpdate === 'function') {
            this.onUIUpdate({ type, source });
        }

        // Dispatch DOM event
        const event = new CustomEvent('hvp_ui_update', {
            bubbles: true,
            detail: { type, source }
        });
        window.dispatchEvent(event);
    },
};

export default SchedulerFlushMethods;
